import { useState } from "react";

import { Footer } from "@/components/landing/footer";
import { LegalContactSection } from "@/components/legal/legal-contact-section";
import { LegalHeader } from "@/components/legal/legal-header";
import { LegalRoleFilter } from "@/components/legal/legal-role-filter";
import { LegalSectionBlock } from "@/components/legal/legal-section-block";
import {
  filterSectionsByRole,
  isSectionHighlighted,
} from "@/content/legal/filter-sections-by-role";
import { getLegalDocument } from "@/content/legal/legal-documents";
import { LEGAL_LAST_UPDATED, LEGAL_VERSION } from "@/content/legal/legal-meta";
import type {
  LegalDocumentId,
  LegalRoleFilter as LegalRoleFilterValue,
} from "@/content/legal/legal-types";

type LegalDocumentPageProps = {
  documentId: LegalDocumentId;
};

function LegalDocumentPage({ documentId }: LegalDocumentPageProps) {
  const document = getLegalDocument(documentId);
  const [selectedRole, setSelectedRole] = useState<LegalRoleFilterValue>("ALL");

  const visibleSections = filterSectionsByRole(document.sections, selectedRole);

  return (
    <main className="landing-light min-h-screen bg-background text-foreground">
      <LegalHeader />

      <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8 sm:py-20 lg:px-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
          Documento legal
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[0.98] tracking-[-0.04em] sm:text-5xl">
          {document.title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Última atualização: {LEGAL_LAST_UPDATED} · Versão {LEGAL_VERSION}
        </p>

        <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Aviso</p>
          <p className="mt-1 text-sm leading-6 text-foreground/80">{document.disclaimer}</p>
        </div>

        <div className="mt-8">
          <LegalRoleFilter selectedRole={selectedRole} onSelectRole={setSelectedRole} />
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[0.3fr_0.7fr] lg:items-start">
          <nav aria-label="Sumário" className="lg:sticky lg:top-24">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Sumário
            </p>
            <ul className="mt-3 space-y-2">
              {visibleSections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-sm text-foreground/70 transition-colors hover:text-primary"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-5">
            {visibleSections.map((section) => (
              <LegalSectionBlock
                key={section.id}
                section={section}
                highlighted={isSectionHighlighted(section, selectedRole)}
              />
            ))}

            {document.id === "privacy" ? (
              <LegalContactSection documentTitle={document.title} />
            ) : null}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}

export { LegalDocumentPage };
